import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { getAirportDetails } from "@/data/airportDetails";
import { MapPin, Clock, Car, Train, ShieldCheck, Plane, Users } from "lucide-react";

interface CrawlableContentProps {
  airportCode: string;
  airportName: string;
  city: string;
}

/**
 * SEO-focused crawlable content section that renders airport details
 * directly in the DOM (not behind tabs) so search engines index it fully.
 */
export const CrawlableContent = ({ airportCode, airportName, city }: CrawlableContentProps) => {
  const details = getAirportDetails(airportCode);
  if (!details) return null;

  const cityName = city.split(",")[0];

  return (
    <section aria-label={`${airportCode} Airport Information`}>
      {/* Quick Facts Grid */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Plane className="h-5 w-5 text-primary" />
            {airportCode} Quick Facts
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 rounded-lg bg-secondary/50 text-center">
              <Users className="h-5 w-5 mx-auto mb-1 text-primary" />
              <p className="text-xs text-muted-foreground">Annual Passengers</p>
              <p className="font-bold text-sm">{details.annualPassengers}M</p>
            </div>
            <div className="p-3 rounded-lg bg-secondary/50 text-center">
              <MapPin className="h-5 w-5 mx-auto mb-1 text-primary" />
              <p className="text-xs text-muted-foreground">Terminals</p>
              <p className="font-bold text-sm">{details.terminals}</p>
            </div>
            <div className="p-3 rounded-lg bg-secondary/50 text-center">
              <Clock className="h-5 w-5 mx-auto mb-1 text-primary" />
              <p className="text-xs text-muted-foreground">Avg Domestic Wait</p>
              <p className="font-bold text-sm">{details.avgDomesticWait}</p>
            </div>
            <div className="p-3 rounded-lg bg-secondary/50 text-center">
              <ShieldCheck className="h-5 w-5 mx-auto mb-1 text-primary" />
              <p className="text-xs text-muted-foreground">PreCheck</p>
              <p className="font-bold text-sm">{details.preCheckLanes ? "Available" : "N/A"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Peak Hours & Parking (crawlable text) */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardContent className="pt-6">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              {airportCode} Peak Hours
            </h3>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-muted-foreground">Morning Rush</dt>
                <dd className="font-medium">{details.peakHours.morning}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Evening Rush</dt>
                <dd className="font-medium">{details.peakHours.evening}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Busiest Days</dt>
                <dd className="font-medium">{details.peakHours.busiestDays.join(", ")}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">International Wait</dt>
                <dd className="font-medium">{details.avgInternationalWait}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <Car className="h-4 w-4 text-primary" />
              {airportCode} Parking Rates
            </h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Economy Lot</dt>
                <dd className="font-medium">{details.parking.economy}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Parking Garage</dt>
                <dd className="font-medium">{details.parking.garage}</dd>
              </div>
              {details.parking.valet && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Valet Parking</dt>
                  <dd className="font-medium">{details.parking.valet}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* Transit Options */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <h3 className="font-bold mb-3 flex items-center gap-2">
            <Train className="h-4 w-4 text-primary" />
            How to Get to {airportName}
          </h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {details.transitOptions.map((option, i) => (
              <li key={i} className="flex items-center gap-2 text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                {option}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Hub Airlines */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <h3 className="font-bold mb-3 flex items-center gap-2">
            <Plane className="h-4 w-4 text-primary" />
            Airlines at {airportCode}
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            {airportName} serves as a hub for {details.hubAirlines.join(" and ")}.
            The airport handles {details.annualPassengers} million passengers annually across {details.terminals} terminal{details.terminals > 1 ? "s" : ""}: {details.terminalNames.join(", ")}.
          </p>
          <div className="flex flex-wrap gap-2">
            {details.preCheckLanes && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-success/10 text-success">
                <ShieldCheck className="h-3 w-3" /> TSA PreCheck Available
              </span>
            )}
            {details.clearLanes && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary">
                CLEAR Lanes Available
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Security Tips (crawlable prose) */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <h3 className="font-bold mb-3">{airportCode} Security Tips</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {details.tipsSecurity.map((tip, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-primary font-bold flex-shrink-0">{i + 1}.</span>
                {tip}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Internal links to guide and related pages */}
      <nav aria-label="Related pages" className="flex flex-wrap gap-3 text-sm">
        <Link
          to={`/airports/${airportCode.toLowerCase()}/guide`}
          className="text-primary hover:underline"
        >
          {airportCode} Complete Airport Guide →
        </Link>
        <Link to="/tips/minimize-wait-times" className="text-primary hover:underline">
          5 Tips to Minimize Wait Times →
        </Link>
        <Link to="/real-time-wait-times" className="text-primary hover:underline">
          How Real-Time Wait Times Work →
        </Link>
        <Link to="/blog" className="text-primary hover:underline">
          Travel Blog →
        </Link>
      </nav>
    </section>
  );
};
